// ============================================================
// 签到状态管理 Hook — 完全走后端接口，不依赖 localStorage
// ============================================================

import { useState, useCallback, useEffect } from 'react'
import { SIGN_IN_REWARDS } from '@task-bazaar/shared'
import { signInApi } from '@task-bazaar/api'
import { useAuthStore } from '../stores/auth.store'

// ─── 工具函数 ──────────────────────────────────────────────

/** 获取当前月份字符串，如 "2026-06" */
function getCurrentMonth(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

/** 根据连续签到天数计算今日奖励 */
function getReward(consecutiveDays: number): number {
  const weekDay = (consecutiveDays % 7) || 7
  return SIGN_IN_REWARDS[weekDay] || 1
}

// ─── Hook ───────────────────────────────────────────────────

export interface UseSignInReturn {
  /** 今日是否已签到 */
  isSigned: boolean
  /** 连续签到天数 */
  consecutiveDays: number
  /** 今日签到奖励 */
  todayReward: number
  /** 本月已签到的日期列表（如 [1, 2, 3] 表示本月 1、2、3 号已签到） */
  signedDays: number[]
  /** 是否显示签到弹窗 */
  showModal: boolean
  /** 执行签到 */
  signIn: () => Promise<void>
  /** 签到中 */
  loading: boolean
  /** 打开弹窗 */
  openModal: () => void
  /** 关闭弹窗 */
  closeModal: () => void
}

export function useSignIn(isLoggedIn: boolean = false): UseSignInReturn {
  const { loadProfile } = useAuthStore()
  const [isSigned, setIsSigned] = useState(false)
  const [consecutiveDays, setConsecutiveDays] = useState(0)
  const [signedDays, setSignedDays] = useState<number[]>([])
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)

  // 计算今日奖励
  const todayReward = getReward(consecutiveDays + (isSigned ? 0 : 1))

  // 登录后从后端获取签到状态
  useEffect(() => {
    if (!isLoggedIn) {
      setIsSigned(false)
      setConsecutiveDays(0)
      return
    }

    // 调用后端接口获取本月签到日历，拿到真实的连续天数和今日签到状态
    signInApi
      .getCalendar(getCurrentMonth())
      .then((res) => {
        const data = res.data
        setIsSigned(data.todaySigned)
        setConsecutiveDays(data.consecutiveDays)
        setSignedDays(data.signedDays || [])
      })
      .catch((err) => {
        console.error('获取签到状态失败:', err)
      })
  }, [isLoggedIn])

  // 未登录用户不显示弹窗；已登录且今日未签到则延迟弹出
  useEffect(() => {
    if (isLoggedIn && !isSigned) {
      const timer = setTimeout(() => {
        setShowModal(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isLoggedIn, isSigned])

  // 监听外部触发的打开事件
  useEffect(() => {
    const handleOpen = () => {
      if (!isLoggedIn) return
      setShowModal(true)
    }
    window.addEventListener('sign-in-modal:open', handleOpen)
    return () => window.removeEventListener('sign-in-modal:open', handleOpen)
  }, [isLoggedIn])

  // 签到 — 调后端接口
  const signIn = useCallback(async () => {
    if (isSigned || loading || !isLoggedIn) return

    setLoading(true)
    try {
      const response = await signInApi.signIn()
      const result = response.data

      setIsSigned(true)
      setConsecutiveDays(result.consecutiveDays)
      // 签到后刷新用户余额
      loadProfile()
      // 签到后刷新本月签到日历
      signInApi.getCalendar(getCurrentMonth()).then((res) => {
        setSignedDays(res.data.signedDays || [])
      })

      // 签到成功后立即关闭弹窗
      setShowModal(false)
    } catch (error: any) {
      console.error('签到失败:', error)
    } finally {
      setLoading(false)
    }
  }, [isSigned, loading, isLoggedIn])

  // 打开弹窗
  const openModal = useCallback(() => {
    if (isLoggedIn && !isSigned) {
      setShowModal(true)
    }
  }, [isLoggedIn, isSigned])

  // 关闭弹窗
  const closeModal = useCallback(() => {
    setShowModal(false)
  }, [])

  return {
    isSigned,
    consecutiveDays,
    todayReward,
    signedDays,
    showModal,
    signIn,
    loading,
    openModal,
    closeModal,
  }
}

// ─── 外部触发工具函数 ───────────────────────────────────────

/** 从任意位置触发签到弹窗 */
export function triggerSignInModal() {
  window.dispatchEvent(new CustomEvent('sign-in-modal:open'))
}
